import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { X, TrendingUp, MapPin, ExternalLink, Loader2, Sparkle, ArrowUp } from 'lucide-react';
import { toast } from 'sonner';
import useUserUuid from './useUserUuid';

import { getBackendUrl } from '../../lib/runtimeConfig';

const API = `${getBackendUrl()}/api`;

/**
 * Right-side drawer that surfaces the top 10 most-popular saved itineraries
 * so users can browse other people's trips, upvote the ones they like, and
 * deep-link into `/trip/{slug}` to see the full route. This is what turns
 * the planner from a single-player tool into a social-proof community —
 * the homepage trip-counter chip is the entry point.
 *
 * The vote endpoint is toggle-safe per `(slug, user_uuid)` — the upvote
 * arrow fills when the user has voted, and clicking again removes the vote.
 * Optimistic update on click; on backend failure we toast + revert.
 *
 * Ordering: `vote_count DESC, created_at DESC` — highest-voted trips rise
 * to the top, but brand-new zero-vote trips still appear so the social
 * loop can bootstrap. The backend returns exactly 10 trips.
 */
const TrendingTripsSheet = ({ open, onClose }) => {
  const { uuid } = useUserUuid();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [votedSet, setVotedSet] = useState(new Set());
  const [pendingSlug, setPendingSlug] = useState(null);

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      // Backend now always returns top 10 by (vote_count DESC, created_at DESC).
      // Zero-vote trips still appear so the leaderboard bootstraps from empty.
      const res = await axios.get(`${API}/ai-search/trips/popular`, {
        params: { limit: 10 },
        timeout: 12000,
      });
      const next = res?.data?.trips || [];
      setTrips(next);
    } catch {
      setTrips([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Hydrate "trips this user has already voted on" from localStorage so the
  // hearts render filled across refreshes without an extra backend round trip.
  useEffect(() => {
    if (!open) return;
    try {
      const raw = localStorage.getItem('wf_voted_slugs');
      if (raw) setVotedSet(new Set(JSON.parse(raw)));
    } catch {
      // ignore
    }
    fetchTrips();
  }, [open, fetchTrips]);

  // Body scroll-lock while the sheet is open so the page underneath stays put.
  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Esc to close.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const persistVotedSet = (set) => {
    try {
      localStorage.setItem('wf_voted_slugs', JSON.stringify(Array.from(set)));
    } catch {
      // ignore
    }
  };

  const handleVote = async (slug) => {
    if (!uuid || pendingSlug) return;
    setPendingSlug(slug);
    const wasVoted = votedSet.has(slug);

    // Optimistic update — flip the heart + bump count immediately.
    setVotedSet((prev) => {
      const next = new Set(prev);
      if (wasVoted) next.delete(slug);
      else next.add(slug);
      persistVotedSet(next);
      return next;
    });
    setTrips((prev) =>
      prev.map((t) =>
        t.slug === slug
          ? { ...t, vote_count: Math.max(0, (t.vote_count || 0) + (wasVoted ? -1 : 1)) }
          : t,
      ),
    );

    try {
      const res = await axios.post(
        `${API}/ai-search/trips/${slug}/vote`,
        { user_uuid: uuid },
        { timeout: 8000 },
      );
      // Reconcile to authoritative count from server.
      const serverCount = res?.data?.vote_count;
      const serverVoted = res?.data?.voted;
      if (typeof serverCount === 'number') {
        setTrips((prev) => prev.map((t) => (t.slug === slug ? { ...t, vote_count: serverCount } : t)));
      }
      if (typeof serverVoted === 'boolean') {
        setVotedSet((prev) => {
          const next = new Set(prev);
          if (serverVoted) next.add(slug);
          else next.delete(slug);
          persistVotedSet(next);
          return next;
        });
      }
    } catch {
      // Revert optimistic update on failure.
      setVotedSet((prev) => {
        const next = new Set(prev);
        if (wasVoted) next.add(slug);
        else next.delete(slug);
        persistVotedSet(next);
        return next;
      });
      setTrips((prev) =>
        prev.map((t) =>
          t.slug === slug
            ? { ...t, vote_count: Math.max(0, (t.vote_count || 0) + (wasVoted ? 1 : -1)) }
            : t,
        ),
      );
      toast.error('Could not record your vote — please try again.');
    } finally {
      setPendingSlug(null);
    }
  };

  // We portal the entire overlay to document.body so it escapes any
  // transformed/animated ancestor (e.g. the hero column's `animate-fade-in`
  // wrapper leaves a residual `transform: matrix()` that establishes a new
  // containing block, which traps fixed-position children). Without this
  // portal, the dim/blur backdrop only covers the right column and clicking
  // the page background does NOT close the drawer.
  const overlay = (
    <div
      className={`fixed inset-0 z-[80] ${open ? 'pointer-events-auto' : 'pointer-events-none'}`}
      aria-hidden={!open}
      data-testid="trending-trips-sheet"
    >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
        data-testid="trending-backdrop"
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={`absolute right-0 top-0 h-full w-full sm:w-[440px] max-w-[100vw] bg-gradient-to-b from-[#1a1a2e] to-[#0f3460] text-white shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="trending-trips-title"
      >
        {/* Header */}
        <div className="shrink-0 px-5 pt-5 pb-4 border-b border-white/10">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-gold-300 mb-1.5">
                <TrendingUp className="w-3.5 h-3.5" />
                Top 10 · All-time leaderboard
              </div>
              <h2
                id="trending-trips-title"
                className="font-serif text-2xl leading-tight"
              >
                Trending wine trips
              </h2>
              <p className="text-white/60 text-xs mt-1">
                The 10 most-voted itineraries planned by the community. Upvote the ones you love — top votes rise to the top.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center transition-colors"
              aria-label="Close trending trips"
              data-testid="trending-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2.5" data-testid="trending-list">
          {loading && (
            <div className="flex flex-col items-center justify-center h-40 text-white/60 text-sm gap-2">
              <Loader2 className="w-5 h-5 animate-spin text-gold-300" />
              Loading trending trips…
            </div>
          )}

          {!loading && trips.length === 0 && (
            <div className="flex flex-col items-center justify-center h-40 text-white/60 text-sm text-center px-6">
              <Sparkle className="w-6 h-6 text-gold-300 mb-2" />
              <p>No trips saved yet.</p>
              <p className="mt-1 text-xs">Be the first — plan one and it&apos;ll show up here for everyone to vote on.</p>
            </div>
          )}

          {!loading &&
            trips.map((t, idx) => {
              const voted = votedSet.has(t.slug);
              const isPending = pendingSlug === t.slug;
              return (
                <article
                  key={t.slug}
                  className="rounded-xl bg-white/5 border border-white/10 hover:border-gold-400/50 transition-colors p-3.5"
                  data-testid={`trending-card-${t.slug}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-[0.18em] text-gold-300 mb-1">
                        <span className="text-white/40">#{idx + 1}</span>
                        <span>·</span>
                        <span>{new Date(t.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                      </div>
                      <Link
                        to={`/trip/${t.slug}`}
                        onClick={onClose}
                        className="block text-sm font-semibold text-white hover:text-gold-300 transition-colors line-clamp-2"
                        data-testid={`trending-title-${t.slug}`}
                        title={t.query}
                      >
                        &ldquo;{t.query}&rdquo;
                      </Link>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleVote(t.slug)}
                      disabled={isPending}
                      className={`shrink-0 inline-flex flex-col items-center justify-center min-w-[3.25rem] gap-0.5 px-2 py-2 rounded-lg border transition-all ${
                        voted
                          ? 'bg-gold-500/25 border-gold-400 text-gold-100 shadow-lg shadow-gold-500/20'
                          : 'bg-white/5 border-white/15 hover:border-gold-400 hover:bg-gold-500/15 text-white/80'
                      } disabled:opacity-60 disabled:cursor-not-allowed`}
                      aria-label={voted ? 'Remove your upvote' : 'Upvote this trip'}
                      aria-pressed={voted}
                      data-testid={`trending-vote-${t.slug}`}
                    >
                      {isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ArrowUp
                          className={`w-4 h-4 transition-transform ${voted ? 'text-gold-200 scale-110 -translate-y-px' : ''}`}
                          strokeWidth={2.5}
                        />
                      )}
                      <span className={`text-[11px] font-bold tabular-nums leading-none ${voted ? 'text-gold-100' : ''}`}>{t.vote_count}</span>
                      <span className={`text-[8px] font-bold uppercase tracking-wider leading-none mt-0.5 ${voted ? 'text-gold-200' : 'text-white/40'}`}>
                        {voted ? 'Voted' : 'Upvote'}
                      </span>
                    </button>
                  </div>

                  {/* Stop preview */}
                  {t.stop_names?.length > 0 && (
                    <ol className="mt-2.5 space-y-1">
                      {t.stop_names.map((name, i) => (
                        <li key={`${t.slug}-${i}`} className="flex items-start gap-1.5 text-[11px] text-white/70 leading-snug">
                          <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-gold-300" />
                          <span className="min-w-0 break-words">
                            <span className="text-white/40 mr-1">{i + 1}.</span>
                            {name}
                          </span>
                        </li>
                      ))}
                    </ol>
                  )}

                  <Link
                    to={`/trip/${t.slug}`}
                    onClick={onClose}
                    className="mt-2.5 inline-flex items-center gap-1 text-[11px] font-semibold text-gold-300 hover:text-gold-200 transition-colors"
                    data-testid={`trending-open-${t.slug}`}
                  >
                    Open trip plan <ExternalLink className="w-3 h-3" />
                  </Link>
                </article>
              );
            })}
        </div>

        {/* Footer hint */}
        <div className="shrink-0 px-5 py-3 border-t border-white/10 text-[10px] text-white/40 text-center">
          Upvote the trips you love. One vote per trip — click again to undo.
        </div>
      </aside>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(overlay, document.body);
};

export default TrendingTripsSheet;
